/**
 * Video Aura — 純ロジック
 *
 * 設定解決 / dt非依存フェード / 発生源レイアウト。
 * Three依存を含まないため vitest で数値挙動を固定できる。
 */

export const AURA_DEFAULTS = Object.freeze({
  coverage: 0.4,
  intensity: 0.8,
  tint: [1.0, 1.0, 1.0],
  speed: 1.0,
  spread: 2.2,
  fadeMs: 500,
});

const COVERAGE_RANGE = [0, 1];
const INTENSITY_RANGE = [0, 5];
const SPEED_RANGE = [0.1, 5];
const SPREAD_RANGE = [1.5, 8];
const FADEMS_RANGE = [0, 10000];

/**
 * オーラ設定を解決する。
 *
 * **デフォルトON（オプトアウト方式）**: 全案件で発光が付く（CEO決定 2026-07-15）。
 * 切りたい案件だけ posters.json で `effects.aura.enabled: false` を明示する。
 *
 * 優先順位（高→低）:
 *   1. URL パラメータ `?fx=aura`（enabled=false さえ上書きする開発強制ON）/ `?cov= ?int= ?spread=`
 *   2. posterData.effects.aura の値
 *   3. AURA_DEFAULTS
 *
 * @returns {Object|null} 有効化された設定、null（enabled:false でオプトアウトされた案件）
 */
export function resolveAuraConfig(posterData, urlParams) {
  const params = urlParams instanceof URLSearchParams ? urlParams : new URLSearchParams();
  const urlForceOn = params.get('fx') === 'aura';
  const auraRaw = posterData?.effects?.aura;

  if (auraRaw?.enabled === false && !urlForceOn) return null;

  const src = auraRaw ?? {};
  const urlCov = parseFiniteNumber(params.get('cov'));
  const urlSpread = parseFiniteNumber(params.get('spread'));
  const urlInt = parseFiniteNumber(params.get('int'));

  return {
    coverage:
      urlCov !== null
        ? clamp(urlCov, COVERAGE_RANGE)
        : clampNumber(src.coverage, COVERAGE_RANGE, AURA_DEFAULTS.coverage),
    intensity:
      urlInt !== null
        ? clamp(urlInt, INTENSITY_RANGE)
        : clampNumber(src.intensity, INTENSITY_RANGE, AURA_DEFAULTS.intensity),
    tint: resolveTint(src.tint),
    speed: clampNumber(src.speed, SPEED_RANGE, AURA_DEFAULTS.speed),
    spread:
      urlSpread !== null
        ? clamp(urlSpread, SPREAD_RANGE)
        : clampNumber(src.spread, SPREAD_RANGE, AURA_DEFAULTS.spread),
    fadeMs: clampNumber(src.fadeMs, FADEMS_RANGE, AURA_DEFAULTS.fadeMs),
  };
}

export function createFadeState() {
  return { value: 0 };
}

/**
 * dt 非依存の指数フェード（1ステップ）。**state を in-place で更新する**。
 * setAnimationLoop で毎フレーム走るため、返り値を新オブジェクトにするとGC圧が発生
 * （Issue #26 明記の禁止事項）。呼び出し側は同じ参照を使い続ける。
 *
 * tau = fadeMs / ln(100): fadeMs 経過で残差 1%（体感で「切り替わった」と感じる境目）
 */
export function stepFade(state, targetOn, dtMs, fadeMs) {
  const target = targetOn ? 1 : 0;
  if (fadeMs <= 0) {
    state.value = target;
    return state;
  }
  if (dtMs <= 0) return state;

  const tau = fadeMs / 4.605170185988091;
  const k = 1 - Math.exp(-dtMs / tau);
  state.value += (target - state.value) * k;
  return state;
}

/**
 * 発生源層の寸法。anchor 単位系で正方形（映像アスペクトに依存させると
 * 縦横で広がり方が変わり演出として不安定になる）。
 */
export function computeSourceLayout(spread) {
  if (!isPositiveFinite(spread)) {
    throw new Error(`spread must be a positive number (got ${spread})`);
  }
  return { width: spread, height: spread };
}

function isPositiveFinite(v) {
  return typeof v === 'number' && Number.isFinite(v) && v > 0;
}

function parseFiniteNumber(input) {
  if (input === null || input === undefined) return null;
  const n = Number(input);
  return Number.isFinite(n) ? n : null;
}

function clamp(value, [min, max]) {
  return Math.max(min, Math.min(max, value));
}

function clampNumber(value, range, fallback) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return clamp(value, range);
}

function resolveTint(value) {
  if (!Array.isArray(value) || value.length !== 3) return [...AURA_DEFAULTS.tint];
  return value.map((v) => clampNumber(v, [0, 1], 1));
}
